import Button from "@/comps/button"
import Footer from "@/comps/footer"
import Header from "@/comps/header"
import Input, {
	Textarea,
	useValidator,
} from "@/comps/input"
import {
	ErrorMessage,
	SuccessMessage,
	WarningMessage,
} from "@/comps/message"
import Meta from "@/comps/meta"
import Tooltip from "@/comps/tooltip"

import { generateServerSideProps } from "@/ssr/common"
import { axios } from "@/utils/axios-wrapper"

import { devices } from "@/utils/breakpoints"
import { NotificationContext } from "@/utils/notification"
import { isEmail, notEmpty } from "@/utils/validators"

import { AxiosError } from "axios"
import { ContactRequest } from "models/general"
import { useTranslation } from "next-i18next"
import {
	FormEvent,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react"
import { Box } from "react-styled-flex"
import styled from "styled-components"

const Wrapper = styled.div`
	margin: 0 auto;

	width: calc(var(--w-screen) - 32px);
	min-height: calc(100vh - 540px);

	@media ${devices.tablet} {
		width: 605px;
	}

	@media ${devices.desktop} {
		width: 1175px;
	}
`

const Container = styled.div`
	width: 100%;

	display: flex;
	flex-direction: column;

	align-items: center;
	justify-content: center;

	gap: 16px;

	@media ${devices.desktop} {
		gap: 25px;
	}
`

const Title = styled.h1`
	text-transform: capitalize;
	text-align: center;

	font-weight: 700;
	font-size: 18px;
	line-height: 21px;

	margin: 16px 0;

	@media ${devices.tablet} {
		font-size: 24px;
		line-height: 29px;

		margin: 12px 0 21px 0;
	}

	@media ${devices.desktop} {
		font-weight: 800;
		font-size: 36px;
		line-height: 52px;

		margin-bottom: 12px;
	}
`

const Block = styled.div`
	display: flex;
	flex-direction: column;

	gap: 16px;

	padding: 24px;
	border-radius: 12px;

	background-color: var(--color-background-cell);
`

const Label = styled.h2`
	font-weight: 600;
	font-size: 18px;
	line-height: 21px;

	@media ${devices.tablet} {
		font-size: 22px;
		line-height: 26px;
	}
`

const TextContent = styled.p`
	font-weight: 400;
	font-size: 16px;
	line-height: 25px;

	@media ${devices.tablet} {
		font-weight: 500;
	}
`

const ContactTextContent = styled(TextContent)`
	max-width: 300px;
`

const ContactForm = styled(Block)`
	width: 100%;

	@media ${devices.tablet} {
		width: 605px;
	}

	@media ${devices.desktop} {
		width: 375px;
	}
`

const InputContainer = styled.form`
	display: flex;
	flex-direction: column;
`

const TabletRow = styled.div`
	display: flex;
	flex-direction: column;

	@media ${devices.tablet} {
		flex-direction: row;
		gap: 24px;
	}

	@media ${devices.desktop} {
		flex-direction: column;
		gap: 0;
	}
`

const NameInput = styled(Input)`
	input {
		text-transform: capitalize;
	}

	flex: 1;
`

const PhoneInput = styled(Input)`
	flex: 1;
`

const Contact = () => {
	const { t } = useTranslation("page-contact")

	const [name, setName] = useState("")
	const [phone, setPhone] = useState("")
	const [email, setEmail] = useState("")
	const [message, setMessage] = useState("")
	const [loading, setLoading] = useState(false)

	const { validate: nameValidate, errors: nameErrors } =
		useValidator(name, [notEmpty])

	const { validate: emailValidate, errors: emailErrors } =
		useValidator(email, [notEmpty, isEmail])
	const {
		validate: messageValidate,
		errors: messageErrors,
	} = useValidator(message, [notEmpty])

	const formRef = useRef<HTMLFormElement>(null)

	const { notify } = useContext(NotificationContext)

	const anyError = !(
		name &&
		email &&
		message &&
		!nameErrors &&
		!emailErrors &&
		!messageErrors
	)

	const submitHandler = useCallback(
		async (e: FormEvent) => {
			e.preventDefault()

			if (loading)
				return notify(
					<WarningMessage>
						{t("common:txt_loading_wait")}
					</WarningMessage>,
				)

			if (anyError)
				return notify(
					<WarningMessage>
						{t("common:txt_please_fill_input")}
					</WarningMessage>,
				)

			setLoading(true)

			try {
				const data: ContactRequest = {
					name,
					phone: phone || null,
					email,
					message,
				}

				await axios.post("/api/v1/contact/submit", data)

				notify(
					<SuccessMessage>
						{t("common:txt_contact_success")}
					</SuccessMessage>,
				)
			} catch (e) {
				if (
					e instanceof AxiosError &&
					e.response?.data.code === "logic"
				) {
					notify(
						<ErrorMessage>
							{t("common:txt_captcha_failed")}
						</ErrorMessage>,
					)
				} else {
					notify(
						<ErrorMessage>
							{t("common:txt_contact_error")}
						</ErrorMessage>,
					)
				}
			}

			setLoading(false)
			setName("")
			setPhone("")
			setEmail("")
			setMessage("")
		},
		[
			anyError,
			email,
			loading,
			message,
			name,
			notify,
			phone,
			t,
		],
	)

	return (
		<>
			<Meta
				title={t("page-contact:txt_page_title")}
				description={t("page-contact:txt_page_description")}
			/>
			<Header />
			<Wrapper>
				<Title>
					{t("lbl_contact_us", { ns: "common" })}
				</Title>
				<Container>
					<span className="text-[20px] font-400 text-blue-700">
						{t("txt_we_are_here")}
					</span>

					<ContactForm>
						<Label>{t("common:txt_get_in_touch")}</Label>
						<ContactTextContent>
							{t("txt_have_an_inquiry")}
						</ContactTextContent>
						<InputContainer
							onSubmit={submitHandler}
							ref={formRef}>
							<TabletRow>
								<NameInput
									validate={nameValidate}
									errors={nameErrors}
									value={name}
									setValue={setName}
									label={t("common:txt_full_name")}
									type="text"
									name="name"
								/>
								<PhoneInput
									value={phone}
									setValue={setPhone}
									label={t(
										"common:txt_phone_number_optional",
									)}
									type="tel"
									name="phone"
								/>
							</TabletRow>
							<Input
								validate={emailValidate}
								errors={emailErrors}
								value={email}
								setValue={setEmail}
								label={t("common:txt_email_addr")}
								type="email"
								name="email"
							/>
							<Textarea
								validate={messageValidate}
								errors={messageErrors}
								label={t("txt_your_message")}
								value={message}
								setValue={setMessage}
								rows={3}
							/>
							<Box height={12} />
							<Tooltip
								title={t("common:txt_please_fill")}
								disabled={Boolean(anyError)}>
								<Button
									loading={loading}
									disabled={Boolean(anyError) || loading}
									primary
									text="page-contact:lbl_submit"
								/>
							</Tooltip>
						</InputContainer>
					</ContactForm>

					<div className="flex flex-col items-center justify-center">
						<span className="text-[16px] font-400 text-blue-800">
							{t("txt_contact_any_questions")}
						</span>
						<a
							href="mailto:info@vidsup.com"
							className="text-[16px] font-500 text-blue-800">
							info@vidsup.com
						</a>
					</div>
				</Container>
			</Wrapper>
			<Footer />
		</>
	)
}

export const getServerSideProps = generateServerSideProps([
	"page-contact",
])

export default Contact
